import { IListCasesParams, listAllCases } from '@brands/services/cases/listAllCases';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { assignCareAdvisor, IAssignCACase } from '../services/cases/assignCareAdvisor';
import { assignProvider, IAssignProviderCase } from '../services/cases/assignProvider';
import { CasePayload, createNewCase } from '../services/cases/createNewCase';
import { editCaseById } from '../services/cases/editCase';
import { editCaseType } from '../services/cases/editCaseType';
import { getOneCase } from '../services/cases/getCaseById';
import { rescheduleCaseAsStaff } from '../services/cases/rescheduleCase';

import { ICase } from '../services/cases/types/ICase';
import { Meta } from '../services/communication/types/Meta';
import { UserRoleName } from '../services/identity/types/UserProfile';
import { displayErrorDetails, withoutErrorCode } from '../Utils/displayError';

function listErrorFromUnknown(e: unknown): CasesListErrorKind {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 403 || status === 401) return 'forbidden';
  return 'generic';
}

type CursorSnapshot = {
  start: string;
  end: string;
};

export type CasesListErrorKind = 'forbidden' | 'generic' | null;

export interface UseCasesListOptions {
  inlineListErrors?: boolean;
}

interface IUseCases {
  cases: ICase[];
  totalNewCases: number;
  reloadAll: () => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  editCase: (case_id: number, body: CasePayload) => Promise<ICase | null>;
  rescheduleCase: (case_id: number, video_conference_date: string) => Promise<ICase | null>;
  editCaseTypeFn: (case_id: number, body: CasePayload, userRole: string) => Promise<ICase | null>;
  assignToCareAdvisor: (case_id: number, body: IAssignCACase, showError?: boolean) => Promise<ICase | null>;
  assignToProvider: (case_id: number, body: IAssignProviderCase) => Promise<ICase | null>;
  addCase: (payload: CasePayload) => Promise<void>;
  oneCase?: ICase;
  getCaseById: (case_id: number) => Promise<void>;
  dataLoading: boolean;
  loading: boolean;
  pageIndex: number;
  hasNextPage: boolean;
  casesListError: CasesListErrorKind;
  clearCasesListError: () => void;
}

export default function useCases(
  params: IListCasesParams,
  shouldLoadAllCases = false,
  userRoleName?: string,
  listOptions?: UseCasesListOptions,
): IUseCases {
  const [cases, setCases] = useState<ICase[]>([]);
  const [totalNewCases, setTotalNewCases] = useState(0);
  const [oneCase, setOneCase] = useState<ICase | undefined>();
  const [cursorStack, setCursorStack] = useState<CursorSnapshot[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageMeta, setPageMeta] = useState<Meta | null>(null);
  const [casesListError, setCasesListError] = useState<CasesListErrorKind>(null);

  const requestIdRef = useRef(0);
  const inlineListErrors = listOptions?.inlineListErrors === true;

  const clearCasesListError = useCallback(() => {
    setCasesListError(null);
  }, []);

  const buildListParams = useCallback(
    (extra?: Partial<IListCasesParams>): IListCasesParams => {
      return {
        ...params,
        ...(userRoleName ? { userRoleName } : {}),
        ...extra,
      };
    },
    [params, userRoleName],
  );

  const loadInitialPage = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setDataLoading(true);

    try {
      const { nodes, meta, total } = await listAllCases(buildListParams());

      if (requestId !== requestIdRef.current) return;

      setCases(nodes ?? []);
      setPageMeta(meta ?? null);
      setTotalNewCases(total ?? 0);
      setCursorStack([]);
      setCasesListError(null);
    } catch (e) {
      if (requestId === requestIdRef.current) {
        setCases([]);
        setPageMeta(null);
        setTotalNewCases(0);
        setCursorStack([]);
        if (inlineListErrors) {
          setCasesListError(listErrorFromUnknown(e));
        }
      }

      if (!inlineListErrors) {
        displayErrorDetails(e);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setDataLoading(false);
      }
    }
  }, [buildListParams, inlineListErrors]);

  const loadNextPage = useCallback(async () => {
    if (!pageMeta?.has_next_page || !pageMeta?.end_cursor) return;

    const requestId = ++requestIdRef.current;
    const currentSnapshot = {
      start: pageMeta.start_cursor,
      end: pageMeta.end_cursor,
    };

    setDataLoading(true);

    try {
      const { nodes, meta } = await listAllCases(
        buildListParams({
          cursor: pageMeta.end_cursor,
          cursor_direction: 'next',
        }),
      );

      if (requestId !== requestIdRef.current) return;

      setCursorStack((prev) => [...prev, currentSnapshot]);
      setCases(nodes ?? []);
      setPageMeta(meta ?? null);
      setCasesListError(null);
    } catch (e) {
      if (inlineListErrors) {
        setCasesListError(listErrorFromUnknown(e));
      } else {
        displayErrorDetails(e);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setDataLoading(false);
      }
    }
  }, [buildListParams, inlineListErrors, pageMeta]);

  const loadPreviousPage = useCallback(async () => {
    if (cursorStack.length === 0) return;

    // Going back to the first page — the initial load had no cursor, so reload fresh
    if (cursorStack.length === 1) {
      setCursorStack([]);
      await loadInitialPage();
      return;
    }

    if (!pageMeta?.start_cursor) return;

    const requestId = ++requestIdRef.current;
    setDataLoading(true);

    try {
      const { nodes, meta } = await listAllCases(
        buildListParams({
          cursor: pageMeta.start_cursor,
          cursor_direction: 'previous',
        }),
      );

      if (requestId !== requestIdRef.current) return;

      setCases(nodes ?? []);
      setPageMeta(meta ?? null);
      setCursorStack((prev) => prev.slice(0, -1));
      setCasesListError(null);
    } catch (e) {
      if (inlineListErrors) {
        setCasesListError(listErrorFromUnknown(e));
      } else {
        displayErrorDetails(e);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setDataLoading(false);
      }
    }
  }, [buildListParams, cursorStack, inlineListErrors, loadInitialPage, pageMeta]);

  const editCase = async (case_id: number, body: CasePayload): Promise<ICase | null> => {
    try {
      setLoading(true);
      const updated = await editCaseById(case_id, body, userRoleName);
      setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      return updated;
    } catch (e) {
      displayErrorDetails(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Reschedule routes through purpose-built endpoints rather than the
  // generic update_case PATCH. The staff-scoped `update_case` handler skips the
  // tzinfo-strip when the case has no provider (i.e. OHC cases —
  // `(case.provider_id or payload.provider_id)` is false), which surfaces
  // as a 500 to the client even though the row sometimes commits. The
  // dedicated `/care_advisor|collector/reschedule_case/{id}` endpoint always strips
  // tzinfo and handles `care_advisor_id` vs `provider_id` correctly for
  // OHC. See DO-4020.
  const rescheduleCase = async (case_id: number, video_conference_date: string): Promise<ICase | null> => {
    try {
      setLoading(true);
      let updated: ICase;
      if (userRoleName === UserRoleName.CareAdvisor || userRoleName === UserRoleName.Collector) {
        updated = await rescheduleCaseAsStaff(case_id, { video_conference_date }, userRoleName);
      } else {
        updated = await editCaseById(case_id, { video_conference_date } as CasePayload, userRoleName);
      }
      setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      return updated;
    } catch (e) {
      displayErrorDetails(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const editCaseTypeFn = async (case_id: number, body: CasePayload, userRole: string): Promise<ICase | null> => {
    try {
      setLoading(true);
      const updated = await editCaseType(case_id, body, userRole);
      setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      return updated;
    } catch (e: any) {
      toast.error(withoutErrorCode(e?.response?.data?.message) || 'Error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const assignToCareAdvisor = async (
    case_id: number,
    body: IAssignCACase,
    showError = true,
  ): Promise<ICase | null> => {
    try {
      setLoading(true);
      const updated = await assignCareAdvisor(case_id, body, userRoleName);
      setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      return updated;
    } catch (e) {
      if (showError) displayErrorDetails(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const assignToProvider = async (case_id: number, body: IAssignProviderCase): Promise<ICase | null> => {
    try {
      setLoading(true);
      const updated = await assignProvider(case_id, body, userRoleName);
      setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      return updated;
    } catch (e) {
      displayErrorDetails(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addCase = async (payload: CasePayload): Promise<void> => {
    try {
      setLoading(true);
      const created = await createNewCase(payload, userRoleName);
      setCases((prev) => [created, ...prev]);
    } catch (e) {
      displayErrorDetails(e);
    } finally {
      setLoading(false);
    }
  };

  const getCaseById = async (case_id: number): Promise<void> => {
    try {
      setLoading(true);
      const c = await getOneCase(
        case_id,
        userRoleName ? { userRoleName } : undefined,
        userRoleName === UserRoleName.Collector ? UserRoleName.Collector : undefined,
      );
      setOneCase(c);
    } catch (e) {
      displayErrorDetails(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldLoadAllCases) {
      loadInitialPage();
    }
  }, [shouldLoadAllCases, loadInitialPage]);

  return {
    cases,
    totalNewCases,
    reloadAll: loadInitialPage,
    loadNextPage,
    loadPreviousPage,
    editCase,
    rescheduleCase,
    editCaseTypeFn,
    assignToCareAdvisor,
    assignToProvider,
    addCase,
    oneCase,
    getCaseById,
    dataLoading,
    loading,
    pageIndex: cursorStack.length,
    hasNextPage: pageMeta?.has_next_page ?? false,
    casesListError,
    clearCasesListError,
  };
}
